import { TraceConfig, TraceData, SegmentData } from './types';

/**
 * Reporter 类 - 负责将 trace 数据上报到 klog
 * 支持单个 trace 上报和批量 trace 上报功能
 */
export class Reporter {
  private config: TraceConfig | null = null;
  private readonly maxRetries = 3;
  private readonly retryDelay = 1000; // 毫秒

  // 批量上报相关状态
  private traceCache: TraceData[] = [];
  private batchTimerId: number | null = null;
  private isReporting: boolean = false;

  /**
   * 初始化配置
   */
  init(config: TraceConfig): void {
    this.config = config;

    // 启动批量上报（如果启用）
    if (config.batchReport?.enabled !== false) {
      this.startBatchReporting();
    }
  }

  /**
   * 上报 trace 数据
   */
  async report(traceData: TraceData): Promise<void> {
    if (!this.config) {
      console.error('Reporter not initialized');
      return;
    }

    // 如果启用了批量上报，将数据添加到缓存
    if (this.isBatchReportingEnabled()) {
      this.addToCache(traceData);
      return;
    }

    // 否则直接上报
    try {
      const segmentData = this.convertToSegmentFormat(traceData);
      await this.sendData(segmentData);
    } catch (error) {
      console.error('Failed to report trace data:', error);
    }
  }

  /**
   * 创建 Klog 请求
   * 为每个 SegmentData 创建一个独立的 log 对象
   */
  private createKlog(data: string | SegmentData[]): Promise<Response> {
    const token = JSON.parse(localStorage.getItem('_zixel_access_token') || '""');
    const openId = JSON.parse(localStorage.getItem('_zixel_open_id') || '""');
    const organizationId = JSON.parse(localStorage.getItem('_zixel_organization_id') || '""');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      'Zixel-Application-Id': '1002',
      'Zixel-Platform-Id': '1',
    };
    if (token) headers['zixel-auth-token'] = token;
    if (openId) headers['zixel-open-id'] = openId;
    if (organizationId) headers['zixel-organization-id'] = organizationId;

    // 确保 data 总是数组格式
    const segmentDataArray = Array.isArray(data) ? data : [data];

    // 为每个 SegmentData 创建一个独立的 log 对象
    const logs = segmentDataArray.map((segmentData) => ({
      eventData: segmentData,
      logPath: window.location.href,
      logType: 'trace',
      source: 'Frontend',
      timestamp: Date.now(),
      tags: ['traceId', 'spanId'],
    }));

    return fetch(`${this.config?.collectorAddress}/api/klog/v1/post`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        logs,
      }),
    });
  }

  /**
   * 将 TraceData 转换为 SkyWalking Segment 格式
   */
  private convertToSegmentFormat(traceData: TraceData): SegmentData {
    const { spans, traceId, serviceName, serviceInstance, serviceVersion } = traceData;

    const segmentSpans = spans.map((span) => {
      // 使用十六进制字符串格式的 spanId（可以转换为 8 字节二进制）
      const spanId = span.spanId;
      const parentSpanId = span.parentSpanId || '';

      return {
        spanId,
        parentSpanId,
        startTime: span.startTime,
        endTime: span.endTime || span.startTime,
        operationName: span.operationName,
        isError: span.isError || false,
        tags: span.tags.map((tag) => ({
          key: tag.key,
          value: tag.value,
        })),
        logs: span.logs.map((log) => ({
          time: log.timestamp,
          data: log.fields.map((field) => ({
            key: field.key,
            value: field.value,
          })),
        })),
      };
    });

    const { browser, os } = this.getBrowserAndOS();

    return {
      traceId,
      browser,
      os,
      clientHeight: window.innerHeight,
      clientWidth: window.innerWidth,
      pageName: window.document.title,
      referrer: window.location.href,
      userId: window.globalValue?.dataModule?.userInfo?.openId || '',
      service: serviceName,
      serviceInstance: serviceInstance || serviceName,
      spans: segmentSpans,
    };
  }

  /**
   * 获取浏览器和操作系统信息
   */
  private getBrowserAndOS(): { browser: string; os: string } {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';

    // 检测操作系统
    if (ua.indexOf('Windows NT') !== -1) {
      os = 'Windows';
    } else if (ua.indexOf('Mac OS X') !== -1) {
      os = 'macOS';
    } else if (ua.indexOf('Linux') !== -1) {
      os = 'Linux';
    } else if (ua.indexOf('Android') !== -1) {
      os = 'Android';
    } else if (ua.indexOf('iPhone') !== -1 || ua.indexOf('iPad') !== -1) {
      os = 'iOS';
    }

    // 检测浏览器
    if (ua.indexOf('Chrome') !== -1 && ua.indexOf('Edg') === -1) {
      browser = 'Chrome';
    } else if (ua.indexOf('Firefox') !== -1) {
      browser = 'Firefox';
    } else if (ua.indexOf('Safari') !== -1 && ua.indexOf('Chrome') === -1) {
      browser = 'Safari';
    } else if (ua.indexOf('Edg') !== -1) {
      browser = 'Edge';
    } else if (ua.indexOf('Opera') !== -1 || ua.indexOf('OPR') !== -1) {
      browser = 'Opera';
    } else if (ua.indexOf('Trident') !== -1 || ua.indexOf('MSIE') !== -1) {
      browser = 'Internet Explorer';
    }

    return { browser, os };
  }

  /**
   * 生成 Segment ID
   */
  private generateSegmentId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  /**
   * 发送数据到 SkyWalking Collector
   * 支持发送单个 SegmentData 或 SegmentData[] 数组
   */
  private async sendData(segmentData: SegmentData | SegmentData[]): Promise<void> {
    if (!this.config) {
      return;
    }

    // 确保 segmentData 总是数组格式
    const segmentArray = Array.isArray(segmentData) ? segmentData : [segmentData];

    if (this.config.debug) {
      console.log(
        'Reporting trace data:',
        segmentArray.length > 1 ? `${segmentArray.length} traces` : segmentData,
      );
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        const response = await this.createKlog(segmentArray);

        if (response.ok) {
          if (this.config.debug) {
            console.log('Trace data reported successfully');
          }
          return;
        } else {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt < this.maxRetries - 1) {
          // 等待后重试
          await this.delay(this.retryDelay * (attempt + 1));
        }
      }
    }

    // 所有重试都失败
    console.error(`Failed to report trace data after ${this.maxRetries} attempts:`, lastError);
  }

  /**
   * 延迟函数
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * 判断是否启用批量上报
   */
  private isBatchReportingEnabled(): boolean {
    return this.config?.batchReport?.enabled !== false;
  }

  /**
   * 将 TraceData 添加到缓存
   */
  private addToCache(traceData: TraceData): void {
    this.traceCache.push(traceData);

    if (this.config?.debug) {
      console.log(`Trace added to cache. Cache size: ${this.traceCache.length}`);
    }

    // 检查是否达到最大缓存数量，立即上报
    const maxCacheSize = this.config?.batchReport?.maxCacheSize || 5;
    if (this.traceCache.length >= maxCacheSize) {
      if (this.config?.debug) {
        console.log(
          `Cache size reached limit (${maxCacheSize}), triggering immediate batch report`,
        );
      }
      this.performBatchReport();
    }
  }

  /**
   * 启动批量上报
   */
  private startBatchReporting(): void {
    if (this.batchTimerId !== null) {
      return; // 已经在运行
    }

    const interval = this.config?.batchReport?.interval || 60 * 1000; // 默认1分钟

    if (this.config?.debug) {
      console.log(`Starting batch reporting with interval: ${interval}ms`);
    }

    this.batchTimerId = window.setInterval(() => {
      if (this.traceCache.length > 0) {
        this.performBatchReport();
      }
    }, interval) as unknown as number;
  }

  /**
   * 停止批量上报
   */
  private stopBatchReporting(): void {
    if (this.batchTimerId !== null) {
      clearInterval(this.batchTimerId);
      this.batchTimerId = null;

      if (this.config?.debug) {
        console.log('Batch reporting stopped');
      }
    }

    // 上报剩余的缓存数据
    if (this.traceCache.length > 0) {
      this.performBatchReport();
    }
  }

  /**
   * 执行批量上报
   * 将缓存中的所有 trace 转换为 SegmentData 数组，并使用单个 HTTP 请求批量上报
   */
  private async performBatchReport(): Promise<void> {
    if (this.isReporting || this.traceCache.length === 0 || !this.config) {
      return;
    }

    this.isReporting = true;
    const tracesToReport = [...this.traceCache];
    this.traceCache = []; // 清空缓存

    if (this.config.debug) {
      console.log(`Performing batch report with ${tracesToReport.length} traces`);
    }

    try {
      // 将多个 TraceData 转换为 SegmentData 数组
      const segmentDataArray = tracesToReport.map((traceData) =>
        this.convertToSegmentFormat(traceData),
      );

      // 使用单个 HTTP 请求批量上报所有 traces
      await this.sendData(segmentDataArray);

      if (this.config.debug) {
        console.log(`Batch report completed for ${tracesToReport.length} traces`);
      }
    } catch (error) {
      console.error('Batch report failed:', error);
    } finally {
      this.isReporting = false;
    }
  }

  /**
   * 强制执行批量上报（用于清理时）
   */
  forceBatchReport(): Promise<void> {
    return this.performBatchReport();
  }

  /**
   * 获取缓存中的 Trace 数量
   */
  getCacheSize(): number {
    return this.traceCache.length;
  }

  /**
   * 清空缓存
   */
  clearCache(): void {
    this.traceCache = [];
    if (this.config?.debug) {
      console.log('Trace cache cleared');
    }
  }
}
